import { Badge } from '@/components/ui/Badge';

interface NotificationOverviewProps {
  message: string;
  channel: string;
  audience: string;
  recipientsCount: number;
  deliveredCount: number;
  failedCount: number;
  pendingCount: number;
}

/**
 * The send at a glance. Every figure is passed in — there are no defaults, so
 * this card can never draw a number that belongs to nothing.
 */
export function NotificationOverviewCard({
  message,
  channel,
  audience,
  recipientsCount,
  deliveredCount,
  failedCount,
  pendingCount,
}: NotificationOverviewProps) {
  const deliveryPct = recipientsCount > 0 ? Math.round((deliveredCount / recipientsCount) * 100) : 0;
  const failedPct = recipientsCount > 0 ? Math.round((failedCount / recipientsCount) * 100) : 0;
  const pendingPct = recipientsCount > 0 ? Math.round((pendingCount / recipientsCount) * 100) : 0;

  // Circular progress calculations for SVG Donut
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const deliveredOffset = circumference - (deliveryPct / 100) * circumference;

  return (
    <div className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-800 px-5 py-4  shadow-xs flex flex-col gap-3">
      <h2 className="text-lg sm:text-xl font-extrabold text-slate-700 dark:text-white tracking-tight">
        Notification overview
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
        {/* Column 1: Message Quote Box */}
        <div className="bg-slate-50/90 dark:bg-[#0b1322] rounded-2xl p-4 border border-slate-200/90 dark:border-slate-800 flex flex-col justify-between">
          <span className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-extrabold block">
            Message
          </span>
          <p className="text-[13px] font-medium text-slate-700 dark:text-slate-300 leading-relaxed mt-2 line-clamp-3 italic">
            "{message}"
          </p>
        </div>

        {/* Column 2: Channel, Audience, Recipients */}
        <div className="flex flex-col justify-between gap-2.5 bg-slate-50/90 dark:bg-[#0b1322] rounded-2xl p-4 border border-slate-200/90 dark:border-slate-800">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Channel</span>
            <Badge tone={channel === 'SMS' ? 'accent' : 'success'}>
              {channel}
            </Badge>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Audience</span>
            <span className="text-sm font-extrabold text-slate-700 dark:text-slate-200 text-right truncate max-w-[130px]" title={audience}>
              {audience}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-slate-200 dark:border-slate-800">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Recipients</span>
            <span className="text-sm font-black text-slate-900 dark:text-white">{recipientsCount}</span>
          </div>
        </div>

        {/* Column 3: Delivery Status Details */}
        <div className="flex flex-col justify-between gap-2 bg-slate-50/90 dark:bg-[#0b1322] rounded-2xl p-4 border border-slate-200/90 dark:border-slate-800">
          <div>
            <span className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-extrabold block">
              Delivery status
            </span>
            <span className="text-sm font-black text-emerald-600 dark:text-emerald-400 block mt-0.5">
              {deliveryPct}% Delivered
            </span>
          </div>
          <div className="flex items-center justify-between text-xs pt-1.5 border-t border-slate-200 dark:border-slate-800">
            <span className="text-slate-500 dark:text-slate-400 font-bold">Delivered</span>
            <span className="font-black text-slate-700 dark:text-slate-200 text-[13px]">{deliveredCount}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500 dark:text-slate-400 font-bold">Failed</span>
            <span className="font-black text-slate-700 dark:text-slate-200 text-[13px]">{failedCount}</span>
          </div>
        </div>

        {/* Column 4: Donut Ring & Legend */}
        <div className="flex items-center justify-center gap-3.5 bg-slate-50/90 dark:bg-[#0b1322] rounded-2xl p-4 border border-slate-200/90 dark:border-slate-800">
          {/* Donut Chart SVG (constrained fixed size) */}
          <div className="relative h-16 w-16 shrink-0 flex items-center justify-center">
            <svg viewBox="0 0 100 100" className="h-16 w-16 -rotate-90">
              {/* Background Track */}
              <circle
                cx="50"
                cy="50"
                r={radius}
                fill="none"
                strokeWidth="11"
                className="stroke-slate-200 dark:stroke-slate-800"
              />
              {/* Delivered Arc */}
              <circle
                cx="50"
                cy="50"
                r={radius}
                fill="none"
                strokeWidth="11"
                strokeDasharray={circumference}
                strokeDashoffset={deliveredOffset}
                strokeLinecap="round"
                className="stroke-emerald-500 dark:stroke-emerald-400 transition-all duration-700 ease-out"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-sm font-black text-slate-900 dark:text-white leading-none">
                {recipientsCount}
              </span>
              <span className="text-[8px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                Total
              </span>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-col gap-1.5 min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-xs">
              <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
              <span className="text-slate-700 text-sm dark:text-slate-300 font-bold">Delivered</span>
              <span className="text-slate-500 dark:text-slate-400 ml-auto font-mono text-[12px] font-bold whitespace-nowrap pl-1">
                {deliveredCount} ({deliveryPct}%)
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="h-2 w-2 rounded-full bg-rose-500 shrink-0" />
              <span className="text-slate-700 text-sm dark:text-slate-300 font-bold">Failed</span>
              <span className="text-slate-500 dark:text-slate-400 ml-auto font-mono text-[12px] font-bold whitespace-nowrap pl-1">
                {failedCount} ({failedPct}%)
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="h-2 w-2 rounded-full bg-slate-400 shrink-0" />
              <span className="text-slate-700 text-sm dark:text-slate-300 font-bold">Pending</span>
              <span className="text-slate-500 dark:text-slate-400 ml-auto font-mono text-[12px] font-bold whitespace-nowrap pl-1">
                {pendingCount} ({pendingPct}%)
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
